import {AttachmentFile} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit/src/common/utils/testingUtils'
import {HPButton} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast} from '@hm/fundamental'
import {ArrowForward, Close} from '@mui/icons-material'
import {Stack} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../../consts'
import {AttachmentGallery, AttachmentUploadDropzone} from '../../attachments'
import {DownloadSalesForecastExcelTemplate} from '../DownloadSalesForecastExcelTemplate'

import {SalesForecastUploadStepHeader} from './SalesForecastUploadStepHeader'

const MAX_SALES_FORECAST_FILE_SIZE_MB = 10
const AMOUNT_OF_SALES_FORECAST_FILES = 1

interface StepSalesForecastDropzoneProps {
  files: Array<AttachmentFile>
  setFiles: (attachments: AttachmentFile[]) => void
  cancelUpload: () => void
  nextStep: () => void
}

export const StepSalesForecastDropzone: React.FC<StepSalesForecastDropzoneProps> = ({
  files,
  setFiles,
  cancelUpload,
  nextStep
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  return (
    <Stack spacing={1} {...dataTestId('step_sales_forecast_dropzone')}>
      <SalesForecastUploadStepHeader
        title={t('demand.uploadSalesForecastTitle')}
        subtitle={t('demand.uploadSalesForecastDescription')}
      />
      <Stack spacing={1}>
        <AttachmentUploadDropzone
          files={files}
          setFiles={setFiles}
          options={{
            accept: {
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
            },
            maxFiles: AMOUNT_OF_SALES_FORECAST_FILES,
            maxSize: MAX_SALES_FORECAST_FILE_SIZE_MB * 1024 * 1024,
            buttonText: t('common.attachments.uploadFile'),
            dragAndDropText: t('common.attachments.orDragAndDrop')
          }}
          handleError={(errors) => {
            errors.forEach(({errors, file}) => {
              spawnErrorToast(
                <>
                  {t('error.failedToUpload', {name: file.name})}
                  <br />
                  {errors
                    .map((errorCode) =>
                      t(`error.${errorCode}`, {
                        size: MAX_SALES_FORECAST_FILE_SIZE_MB,
                        max: AMOUNT_OF_SALES_FORECAST_FILES
                      })
                    )
                    .join(', ')}
                </>
              )
            })
          }}
        />
        <AttachmentGallery files={files} setFiles={setFiles} />
      </Stack>
      <DownloadSalesForecastExcelTemplate />
      <Stack direction="row" sx={{justifyContent: 'flex-end'}} spacing={1}>
        <HPButton
          color="secondary"
          {...dataTestId('cancel_upload_button')}
          onClick={cancelUpload}
          startIcon={<Close />}
        >
          {t('common.cancel')}
        </HPButton>
        <HPButton
          variant="contained"
          {...dataTestId('continue_upload_button')}
          onClick={nextStep}
          startIcon={<ArrowForward />}
          disabled={files.length === 0}
        >
          {t('common.continue')}
        </HPButton>
      </Stack>
    </Stack>
  )
}
